import {
  createContext,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

export type ElevationRangeKm = readonly [number, number]

export interface ElevationCursorContextValue {
  cursorKm: number | null
  cursorKmRef: MutableRefObject<number | null>
  selectedRangeKm: ElevationRangeKm | null
  setCursorKm: (km: number | null) => void
  setSelectedRangeKm: (range: ElevationRangeKm | null) => void
}

const noop = () => undefined
const defaultCursorRef: MutableRefObject<number | null> = { current: null }

const ElevationCursorContext = createContext<ElevationCursorContextValue>({
  cursorKm: null,
  cursorKmRef: defaultCursorRef,
  selectedRangeKm: null,
  setCursorKm: noop,
  setSelectedRangeKm: noop,
})

export function ElevationCursorProvider({ children }: { children: ReactNode }) {
  const cursorKmRef = useRef<number | null>(null)
  const [cursorKm, setCursorKmState] = useState<number | null>(null)
  const [selectedRangeKm, setSelectedRangeKmState] = useState<ElevationRangeKm | null>(null)

  const setCursorKm = useCallback((next: number | null) => {
    const normalized = next === null || !Number.isFinite(next) ? null : Math.max(0, next)
    cursorKmRef.current = normalized
    setCursorKmState((prev) => {
      if (prev === null && normalized === null) return prev
      if (prev !== null && normalized !== null && Math.abs(prev - normalized) < 0.002) return prev
      return normalized
    })
  }, [])

  const setSelectedRangeKm = useCallback((next: ElevationRangeKm | null) => {
    if (!next) {
      setSelectedRangeKmState(null)
      return
    }
    const from = Math.max(0, Math.min(next[0], next[1]))
    const to = Math.max(0, Math.max(next[0], next[1]))
    setSelectedRangeKmState(to - from < 0.01 ? null : [from, to])
  }, [])

  const value = useMemo<ElevationCursorContextValue>(
    () => ({ cursorKm, cursorKmRef, selectedRangeKm, setCursorKm, setSelectedRangeKm }),
    [cursorKm, selectedRangeKm, setCursorKm, setSelectedRangeKm],
  )

  return <ElevationCursorContext.Provider value={value}>{children}</ElevationCursorContext.Provider>
}

export function useElevationCursor(): ElevationCursorContextValue {
  return useContext(ElevationCursorContext)
}
